import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Layout,
  Modal,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import {
  AddMajor,
  DeleteMajor,
  DuplicateMinor,
  EditMajor,
  RefreshMinor,
} from '@shopify/polaris-icons';
import MontageProfileModal from '../components/modals/MontageProfileModal';
import { montageProfilesService } from '../services/montageProfilesService';

const BUILT_IN_KEYS = new Set(['legacy-static-v1', 'dynamic-story-v1']);

const MontageProfilesPage = ({ setToastMessage, setToastActive }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [modalState, setModalState] = useState({ open: false, mode: 'create', profile: null });
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const showToast = useCallback((message) => {
    setToastMessage?.(message);
    setToastActive?.(true);
  }, [setToastActive, setToastMessage]);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setProfiles(await montageProfilesService.getProfiles({ includeInactive: true }));
    } catch (loadError) {
      setError(loadError.message || 'Failed to load montage profiles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    const openCreate = () => setModalState({ open: true, mode: 'create', profile: null });
    const refresh = () => loadProfiles();
    window.addEventListener('openCreateMontageProfileModal', openCreate);
    window.addEventListener('refreshMontageProfiles', refresh);
    return () => {
      window.removeEventListener('openCreateMontageProfileModal', openCreate);
      window.removeEventListener('refreshMontageProfiles', refresh);
    };
  }, [loadProfiles]);

  const visibleProfiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return profiles;
    return profiles.filter((profile) => (
      String(profile.name || '').toLowerCase().includes(query)
      || String(profile.key || '').toLowerCase().includes(query)
      || String(profile.description || '').toLowerCase().includes(query)
      || (profile.story_types || []).some((type) => type.includes(query))
    ));
  }, [profiles, search]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (modalState.mode === 'create') {
        await montageProfilesService.createProfile(payload);
        showToast('Montage profile created');
      } else {
        await montageProfilesService.updateProfile(modalState.profile.key, payload);
        showToast('Montage profile updated');
      }
      setModalState({ open: false, mode: 'create', profile: null });
      await loadProfiles();
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await montageProfilesService.deactivateProfile(deactivateTarget.key);
      setDeactivateTarget(null);
      showToast('Montage profile deactivated');
      await loadProfiles();
    } catch (deactivateError) {
      setError(deactivateError.message || 'Failed to deactivate montage profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (profile) => {
    setSaving(true);
    try {
      await montageProfilesService.updateProfile(profile.key, { active: true });
      showToast('Montage profile reactivated');
      await loadProfiles();
    } catch (reactivateError) {
      setError(reactivateError.message || 'Failed to reactivate montage profile.');
    } finally {
      setSaving(false);
    }
  };

  const outputLabels = (profile) => Object.keys(profile.outputs || {})
    .map((output) => output.charAt(0).toUpperCase() + output.slice(1));

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          <Banner tone="info">
            Assign each story a default profile and optional printing-lane overrides.
            Generations and orders resolve their lane from those assignments, while all
            dimensions and montage rules stay in this registry.
          </Banner>

          {error && (
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          )}

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <div style={{ minWidth: 280 }}>
                  <TextField
                    label="Search profiles"
                    labelHidden
                    placeholder="Search profiles"
                    value={search}
                    onChange={setSearch}
                    autoComplete="off"
                  />
                </div>
                <InlineStack gap="200">
                  <Button icon={RefreshMinor} onClick={loadProfiles} loading={loading}>
                    Refresh
                  </Button>
                  <Button
                    icon={AddMajor}
                    variant="primary"
                    onClick={() => setModalState({
                      open: true,
                      mode: 'create',
                      profile: null,
                    })}
                  >
                    Add profile
                  </Button>
                </InlineStack>
              </InlineStack>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Profile', 'Story type', 'Outputs', 'Revision', 'Status', 'Actions'].map(
                        (heading) => (
                          <th
                            key={heading}
                            style={{
                              padding: '12px 8px',
                              textAlign: heading === 'Actions' ? 'center' : 'left',
                              borderBottom: '1px solid #e1e3e5',
                              color: '#6d7175',
                              fontSize: 13,
                            }}
                          >
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" style={{ padding: 48, textAlign: 'center' }}>
                          <Spinner size="large" />
                        </td>
                      </tr>
                    ) : visibleProfiles.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ padding: 48, textAlign: 'center' }}>
                          <BlockStack gap="300" align="center">
                            <Text tone="subdued">No montage profiles found.</Text>
                            <Button
                              variant="primary"
                              onClick={() => setModalState({
                                open: true,
                                mode: 'create',
                                profile: null,
                              })}
                            >
                              Add profile
                            </Button>
                          </BlockStack>
                        </td>
                      </tr>
                    ) : visibleProfiles.map((profile) => (
                      <tr key={profile.key}>
                        <td style={{ padding: '14px 8px', borderBottom: '1px solid #f1f2f3' }}>
                          <BlockStack gap="100">
                            <InlineStack gap="200" blockAlign="center">
                              <Text variant="bodyMd" fontWeight="semibold">{profile.name}</Text>
                              {BUILT_IN_KEYS.has(profile.key) && <Badge tone="info">Built-in</Badge>}
                            </InlineStack>
                            <Text variant="bodySm" tone="subdued">{profile.key}</Text>
                            {profile.description && (
                              <Text variant="bodySm" tone="subdued">{profile.description}</Text>
                            )}
                          </BlockStack>
                        </td>
                        <td style={{ padding: '14px 8px', borderBottom: '1px solid #f1f2f3' }}>
                          {(profile.story_types || []).map((type) => (
                            <Badge key={type}>{type.replace(/_/g, ' ')}</Badge>
                          ))}
                        </td>
                        <td style={{ padding: '14px 8px', borderBottom: '1px solid #f1f2f3' }}>
                          <InlineStack gap="100">
                            {outputLabels(profile).map((output) => (
                              <Badge key={output} tone="success">{output}</Badge>
                            ))}
                          </InlineStack>
                        </td>
                        <td style={{ padding: '14px 8px', borderBottom: '1px solid #f1f2f3' }}>
                          {profile.revision}
                        </td>
                        <td style={{ padding: '14px 8px', borderBottom: '1px solid #f1f2f3' }}>
                          <Badge tone={profile.active === false ? 'critical' : 'success'}>
                            {profile.active === false ? 'Inactive' : 'Active'}
                          </Badge>
                        </td>
                        <td
                          style={{
                            padding: '14px 8px',
                            borderBottom: '1px solid #f1f2f3',
                            textAlign: 'center',
                          }}
                        >
                          <InlineStack gap="100" align="center">
                            <Tooltip content="Edit">
                              <Button
                                icon={EditMajor}
                                size="slim"
                                onClick={() => setModalState({
                                  open: true,
                                  mode: 'edit',
                                  profile,
                                })}
                              />
                            </Tooltip>
                            <Tooltip content="Duplicate as a new profile">
                              <Button
                                icon={DuplicateMinor}
                                size="slim"
                                onClick={() => setModalState({
                                  open: true,
                                  mode: 'create',
                                  profile,
                                })}
                              />
                            </Tooltip>
                            {profile.active === false ? (
                              <Button
                                size="slim"
                                onClick={() => handleReactivate(profile)}
                                loading={saving}
                              >
                                Reactivate
                              </Button>
                            ) : !BUILT_IN_KEYS.has(profile.key) && (
                              <Tooltip content="Deactivate">
                                <Button
                                  icon={DeleteMajor}
                                  size="slim"
                                  tone="critical"
                                  onClick={() => setDeactivateTarget(profile)}
                                />
                              </Tooltip>
                            )}
                          </InlineStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>

      <MontageProfileModal
        open={modalState.open}
        mode={modalState.mode}
        profile={modalState.profile}
        profiles={profiles}
        onClose={() => setModalState({ open: false, mode: 'create', profile: null })}
        onSave={handleSave}
        saving={saving}
      />

      <Modal
        open={Boolean(deactivateTarget)}
        onClose={() => setDeactivateTarget(null)}
        title="Deactivate montage profile?"
        primaryAction={{
          content: 'Deactivate',
          destructive: true,
          onAction: handleDeactivate,
          loading: saving,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setDeactivateTarget(null) }]}
      >
        <Modal.Section>
          <Text>
            Deactivation is blocked while any active story is assigned to this profile.
            Reassign those stories first.
          </Text>
        </Modal.Section>
      </Modal>
    </Layout>
  );
};

export default MontageProfilesPage;
